/**
 * TopMoversCard — Iter. 406.m
 *
 * Wall-of-Fame podium showing the community members with the biggest
 * pillar-progression jumps over the past ISO week or calendar month.
 * Uses the public endpoint GET /api/leaderboard/top-movers (auth-only).
 *
 * Visual model
 * ────────────
 * • Compact card with a "Hebdo" / "Mensuel" toggle.
 * • Top 5 athletes with rank pip + pillar icon + display_name +
 *   "+X.X pts" delta badge.
 * • Position 1 highlighted with crown + gold gradient.
 * • Footer line: "Tu es #14 / 87 athletes" pulled from /me endpoint.
 *
 * Re-usable: drop into the Leaderboard page (default), Wall of Fame,
 * Community page, or any place the cohort gathers.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trophy, Crown, Sparkles, TrendingUp, Loader2 } from 'lucide-react';
import { api } from '../context/AppContext';

const RANK_STYLES = {
  1: { ring: 'ring-yellow-400/60', text: 'text-yellow-300', bg: 'bg-yellow-500/15', glow: '0 0 20px rgba(250,204,21,0.45)' },
  2: { ring: 'ring-zinc-300/50', text: 'text-zinc-200', bg: 'bg-zinc-400/15', glow: 'none' },
  3: { ring: 'ring-amber-600/60', text: 'text-amber-400', bg: 'bg-amber-700/15', glow: 'none' },
};

const ROW_STYLES = { ring: 'ring-white/10', text: 'text-white/70', bg: 'bg-white/5', glow: 'none' };

export default function TopMoversCard({ language = 'fr', defaultPeriod = 'weekly' }) {
  const fr = language === 'fr';
  const [period, setPeriod] = useState(defaultPeriod);
  const [data, setData] = useState(null);
  const [meRank, setMeRank] = useState(null);
  const [loading, setLoading] = useState(true);

  const lastLoadedPeriodRef = useRef(null);

  const loadMovers = useCallback(async (currentPeriod) => {
    setLoading(true);
    try {
      const [movers, me] = await Promise.all([
        api.get('/leaderboard/top-movers', { params: { period: currentPeriod, limit: 5 } }),
        api.get('/leaderboard/top-movers/me', { params: { period: currentPeriod } }),
      ]);
      setData(movers?.data || null);
      setMeRank(me?.data || null);
    } catch {
      setData(null);
      setMeRank(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (lastLoadedPeriodRef.current === period) return;
    lastLoadedPeriodRef.current = period;
    loadMovers(period);
  }, [period, loadMovers]);

  const movers = data?.movers || [];
  const isEmpty = !loading && movers.length === 0;

  return (
    <div
      className="relative rounded-2xl bg-gradient-to-br from-violet-500/15 via-emerald-500/10 to-amber-500/15 border-2 border-violet-400/40 shadow-[0_0_40px_rgba(139,92,246,0.25)] backdrop-blur-xl overflow-hidden"
      data-testid="top-movers-card"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-12 -right-12 w-32 h-32 rounded-full bg-violet-500/25 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-12 -left-12 w-32 h-32 rounded-full bg-emerald-500/25 blur-3xl" />

      {/* Header */}
      <div className="relative px-4 pt-4 pb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="shrink-0 w-9 h-9 rounded-xl bg-violet-500/25 border border-violet-300/40 flex items-center justify-center">
            <Trophy className="w-4 h-4 text-violet-200" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-violet-200 font-bold flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              {fr ? 'Top Movers' : 'Top Movers'}
            </p>
            <h3 className="font-heading uppercase text-sm text-white leading-tight truncate">
              {fr ? 'Les plus gros bonds' : 'Biggest leaps'}
            </h3>
          </div>
        </div>

        {/* Period toggle */}
        <div className="shrink-0 flex items-center bg-black/30 rounded-full border border-white/10 p-0.5" data-testid="top-movers-period-toggle">
          {['weekly', 'monthly'].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              data-testid={`top-movers-period-${p}`}
              className={`px-3 py-1 text-[10px] uppercase tracking-widest font-bold rounded-full transition-colors ${
                period === p ? 'bg-violet-500/60 text-white' : 'text-white/60 hover:text-white'
              }`}
            >
              {p === 'weekly' ? (fr ? 'Hebdo' : 'Weekly') : (fr ? 'Mensuel' : 'Monthly')}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="relative px-4 pb-3">
        {loading && (
          <div className="flex items-center justify-center py-8" data-testid="top-movers-loading">
            <Loader2 className="w-5 h-5 text-violet-300 animate-spin" />
          </div>
        )}

        {isEmpty && (
          <div className="text-center py-6" data-testid="top-movers-empty">
            <p className="text-xs text-white/60 leading-relaxed">
              {fr
                ? 'Pas encore de podium pour cette periode.\nReviens dans quelques jours !'
                : 'No podium yet for this period.\nCheck back in a few days!'}
            </p>
          </div>
        )}

        {!loading && movers.length > 0 && (
          <ul className="space-y-1.5" data-testid="top-movers-list">
            {movers.map((m) => {
              const styles = RANK_STYLES[m.rank] || ROW_STYLES;
              return (
                <li
                  key={m.user_id}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl ring-1 ${styles.ring} ${styles.bg}`}
                  style={{ boxShadow: styles.glow }}
                  data-testid={`top-mover-row-${m.rank}`}
                >
                  {/* Rank pip */}
                  <div className={`relative shrink-0 w-7 h-7 rounded-lg flex items-center justify-center font-black text-xs ${styles.bg} ${styles.text} ring-1 ${styles.ring}`}>
                    {m.rank === 1 ? <Crown className="w-3.5 h-3.5" /> : m.rank}
                  </div>

                  {/* Pillar icon */}
                  <div className="shrink-0 w-8 h-8 rounded-lg bg-black/30 flex items-center justify-center text-lg" aria-hidden="true">
                    {m.pillar_icon || '🎯'}
                  </div>

                  {/* Name + pillar */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white truncate leading-tight" data-testid={`top-mover-name-${m.rank}`}>
                      {m.display_name}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-white/55 truncate">
                      {fr ? m.pillar_name_fr : m.pillar_name_en}
                    </p>
                  </div>

                  {/* Delta badge */}
                  <div className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/25 border border-emerald-400/50">
                    <TrendingUp className="w-3 h-3 text-emerald-200" />
                    <span className="text-xs font-black text-emerald-200" data-testid={`top-mover-delta-${m.rank}`}>
                      +{m.delta.toFixed(1)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer — caller's own rank */}
      {!loading && meRank && (meRank.rank || meRank.total_eligible > 0) && (
        <div className="relative px-4 py-2.5 border-t border-white/10 bg-black/20 flex items-center justify-between gap-2" data-testid="top-movers-me">
          <span className="text-[10px] uppercase tracking-widest text-white/60 font-bold">
            {fr ? 'Toi' : 'You'}
          </span>
          <span className="text-xs text-white/80">
            {meRank.rank
              ? (fr
                  ? <>#{meRank.rank} / {meRank.total_eligible} <span className="text-white/50">athletes</span></>
                  : <>#{meRank.rank} / {meRank.total_eligible} <span className="text-white/50">athletes</span></>)
              : (fr ? `${meRank.total_eligible} athletes en lice` : `${meRank.total_eligible} athletes ranked`)}
          </span>
        </div>
      )}
    </div>
  );
}
